import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type {
  ColumnPinningState,
  GroupingColumnMode,
  GroupingState,
  SortingState,
  Table,
  TableState,
} from "@tanstack/react-table";
import {
  type ColumnDef,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { isFunction, noop } from "lodash";

export interface DataTableContext {
  columns: ColumnDef<any, any>[];
  data: any[];
  isLoading: boolean;
  onBottomReached: () => void;
  onChangePage: (page: number) => void;
  onChangeSorting?: (sorting: SortingState) => void;
  onClickRow?: (row: any) => void;
  pageSize: number;
  table: Table<any>;
  totalRecordsCount: number;
}

const DataTableData = createContext<DataTableContext | null>(null);
DataTableData.displayName = "DataTableData";

export const useDataTableDataProvider = () => {
  const context = useContext(DataTableData);
  if (!context) {
    throw new Error(
      "useDataTableDataProvider must be used within a DataTableDataProvider",
    );
  }
  return context;
};

export interface DataTableDataProviderProps {
  children: ReactNode;
  columnPinning?: ColumnPinningState;
  columns?: ColumnDef<any>[];
  data?: any[];
  expanded?: boolean;
  groupedColumnMode?: GroupingColumnMode;
  grouping?: GroupingState;
  groupingEnabled?: boolean;
  initialPage?: number;
  initialSort?: SortingState;
  isLoading?: boolean;
  onBottomReached?: () => void;
  onChangePage?: (page: number) => void;
  onChangeSorting?: (sorting: SortingState) => void;
  onClickRow?: (row: any) => void;
  pageSize?: number;
  totalRecordsCount?: number;
}

interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

export const DataTableDataProvider = ({
  children,
  columnPinning = {},
  columns = [],
  data = [],
  expanded = false,
  groupedColumnMode = "remove",
  grouping = [],
  groupingEnabled = false,
  initialPage = 1,
  initialSort,
  isLoading = false,
  onBottomReached = noop,
  onChangePage = noop,
  onChangeSorting,
  onClickRow,
  pageSize: pageSizeProp = 10,
  totalRecordsCount = 0,
}: DataTableDataProviderProps) => {
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: initialPage - 1,
    pageSize: pageSizeProp,
  });

  useEffect(() => {
    setPagination({
      pageIndex: initialPage - 1,
      pageSize: pageSizeProp,
    });
  }, [initialPage, pageSizeProp, setPagination]);

  const pagination: PaginationState = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize],
  );

  const groupingProps = groupingEnabled
    ? {
        getGroupedRowModel: getGroupedRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        enableGrouping: groupingEnabled,
        groupedColumnMode,
      }
    : {};

  const state: Partial<TableState> = {
    pagination,
    grouping: groupingEnabled ? grouping : undefined,
    expanded: expanded ? expanded : undefined,
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    pageCount: Math.ceil(totalRecordsCount / pageSize) ?? -1,
    state,
    initialState: {
      sorting: initialSort,
      columnPinning,
    },
    onPaginationChange: setPagination,
    ...groupingProps,
  });

  const { sorting: sortingState } = table.getState();

  const [previousIndex, setPreviousIndex] = useState(pageIndex);
  const [previousSorting, setPreviousSorting] = useState(sortingState);

  useEffect(() => {
    if (
      isFunction(onChangeSorting) &&
      sortingState &&
      sortingState !== previousSorting
    ) {
      setPreviousSorting(sortingState);
      onChangeSorting(sortingState);
    }
  }, [onChangeSorting, previousSorting, sortingState]);

  useEffect(() => {
    if (isFunction(onChangePage) && pageIndex !== previousIndex) {
      setPreviousIndex(pageIndex);
      onChangePage(pageIndex + 1);
    }
  }, [onChangePage, pageIndex, previousIndex]);

  const value: DataTableContext = useMemo(
    () => ({
      columns,
      data,
      isLoading,
      onBottomReached,
      onChangePage,
      onChangeSorting,
      onClickRow,
      pageSize,
      table,
      totalRecordsCount,
    }),
    [
      columns,
      data,
      isLoading,
      onBottomReached,
      onChangePage,
      onChangeSorting,
      onClickRow,
      pageSize,
      table,
      totalRecordsCount,
    ],
  );

  return (
    <DataTableData.Provider value={value}>{children}</DataTableData.Provider>
  );
};
